import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CrudPage } from "@/components/CrudPage";
import { StatusBadge } from "@/components/StatusBadge";
import { PROPERTY_TYPES, OWNERSHIP, STRUCTURES, USE_PURPOSE } from "@/lib/constants";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TYPE_MAP = Object.fromEntries(PROPERTY_TYPES.map((t) => [t.value, { label: t.label, tone: "blue" }]));
const OWN_MAP = { owned: { label: "所有", tone: "green" }, managed: { label: "管理受託", tone: "gray" } };

const columns = [
  { key: "name", label: "物件名", render: (r) => <span className="font-medium text-[#0A0A0A]">{r.name}</span> },
  { key: "type", label: "種別", render: (r) => <StatusBadge value={r.type} map={TYPE_MAP} /> },
  { key: "ownership", label: "区分", render: (r) => <StatusBadge value={r.ownership} map={OWN_MAP} /> },
  { key: "address", label: "所在地", render: (r) => <span className="text-muted-foreground">{r.address}</span> },
  { key: "current_value", label: "現在評価額", render: (r, h) => h.yen(r.current_value) },
  { key: "property_tax", label: "固定資産税", render: (r, h) => h.yen(r.property_tax) },
];

const fields = [
  { name: "name", label: "物件名", required: true },
  { name: "type", label: "物件種別", type: "select", options: PROPERTY_TYPES, required: true },
  { name: "ownership", label: "所有区分", type: "select", options: OWNERSHIP },
  { name: "use_purpose", label: "使用目的（契約書 第3条）", type: "select", options: USE_PURPOSE },
  { name: "postal_code", label: "郵便番号" },
  { name: "address", label: "所在地", colSpan: 2 },
  { name: "land_area", label: "土地面積(㎡)", type: "number" },
  { name: "building_area", label: "建物面積(㎡)", type: "number" },
  { name: "structure", label: "構造", type: "select", options: STRUCTURES },
  { name: "floors", label: "階数", type: "number" },
  { name: "built_date", label: "築年月 (YYYY-MM)" },
  { name: "acquired_date", label: "取得日", type: "date" },
  { name: "purchase_price", label: "購入価格", type: "currency" },
  { name: "current_value", label: "現在評価額", type: "currency" },
  { name: "land_price", label: "土地価格", type: "currency" },
  { name: "building_price", label: "建物価格", type: "currency" },
  { name: "property_tax", label: "固定資産税(年)", type: "currency" },
  { name: "management_company", label: "管理会社" },
  { name: "landlord_id", label: "貸主", type: "select", lookup: "landlords" },
  { name: "photo_url", label: "物件写真（メイン）", type: "file" },
  { name: "building_photos", label: "建物写真", type: "gallery" },
  { name: "floor_plans", label: "間取図", type: "gallery" },
  { name: "google_map_url", label: "GoogleマップURL（任意・空欄なら所在地から表示）", colSpan: 2 },
  { name: "note", label: "備考", type: "textarea" },
];

export default function Properties() {
  const navigate = useNavigate();
  const [created, setCreated] = useState(null);
  return (
    <>
      <CrudPage
        title="物件管理"
        subtitle="所有・管理する物件の一覧"
        endpoint="/properties"
        resource="properties"
        columns={columns}
        fields={fields}
        testid="properties"
        addLabel="物件を登録"
        onView={(r) => navigate(`/properties/${r.id}`)}
        onCreated={(p) => setCreated(p)}
        searchKeys={["name", "address", "type"]}
      />
      <AlertDialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <AlertDialogContent data-testid="property-created-prompt">
          <AlertDialogHeader>
            <AlertDialogTitle>物件を登録しました</AlertDialogTitle>
            <AlertDialogDescription>
              続けてこの物件の「部屋・区画」を登録しますか？部屋を登録すると、契約・入居・空室の管理が使えるようになります。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="skip-room-reg">あとで</AlertDialogCancel>
            <AlertDialogAction
              data-testid="go-room-reg"
              onClick={() => { const id = created?.id; setCreated(null); if (id) navigate(`/rooms?property_id=${id}&new=1`); }}
              className="bg-brand hover:bg-brand-hover text-white"
            >
              部屋を登録する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
